import { assign, fromPromise, setup } from 'xstate';
import type { OpportunityFilter } from '~/features/provider-hlx/job-opportunities/types';
import { SERVICE_OPPORTUNITIES_API as api, type ServiceInbox } from './api';

export const serviceOpportunitiesMachine = setup({
  types: {
    context: {} as {
      filter: OpportunityFilter;
      /** [MCV-1850] Declined and withdrawn responses are hidden unless this is set. */
      includeDeclined: boolean;
      /** The response a deep link named; the first full read widens to find it, then drops it. */
      focusResponseId: number | undefined;
      data: ServiceInbox | null;
      error: string | null;
      refreshQueued: boolean;
    },
    input: {} as { focusResponseId?: number } | undefined,
    events: {} as
      | { type: 'FILTER'; filter: OpportunityFilter }
      | { type: 'INCLUDE_DECLINED'; includeDeclined: boolean }
      | { type: 'REFRESH' },
  },
  actors: {
    read: fromPromise(
      async ({
        input,
        signal,
      }: {
        input: { filter: OpportunityFilter; includeDeclined: boolean; focusResponseId?: number };
        signal: AbortSignal;
      }): Promise<{ inbox: ServiceInbox; includeDeclined: boolean }> => {
        const inbox = await api.getAll(input.filter, signal, input.includeDeclined);
        const { focusResponseId } = input;
        // [MCV-1850] A notification can name a response the default list hides
        // (declined or withdrawn). Look again with those included, and keep them so
        // the checkbox matches the list the user ends up with.
        if (
          focusResponseId !== undefined &&
          !input.includeDeclined &&
          !inbox.data.some((row) => row.responseId === focusResponseId)
        ) {
          const wider = await api.getAll(input.filter, signal, true);
          if (wider.data.some((row) => row.responseId === focusResponseId))
            return { inbox: wider, includeDeclined: true };
        }
        return { inbox, includeDeclined: input.includeDeclined };
      }
    ),
    refresh: fromPromise(
      ({
        input,
        signal,
      }: {
        input: { filter: OpportunityFilter; includeDeclined: boolean; current: ServiceInbox };
        signal: AbortSignal;
      }) => api.refreshHead(input.filter, input.current, signal, input.includeDeclined)
    ),
  },
  guards: {
    hasData: ({ context }) => context.data !== null,
  },
}).createMachine({
  id: 'serviceOpportunities',
  initial: 'loading',
  context: ({ input }) => ({
    filter: 'all' as OpportunityFilter,
    includeDeclined: false,
    focusResponseId: input?.focusResponseId,
    data: null,
    error: null,
    refreshQueued: false,
  }),
  on: {
    FILTER: {
      target: '.loading',
      reenter: true,
      actions: assign({ filter: ({ event }) => event.filter, data: null }),
    },
    INCLUDE_DECLINED: {
      target: '.loading',
      reenter: true,
      actions: assign({ includeDeclined: ({ event }) => event.includeDeclined, data: null }),
    },
  },
  states: {
    // A full read: the list is replaced, so the sidebar shows a spinner.
    loading: {
      entry: assign({ error: null, refreshQueued: false }),
      on: { REFRESH: { actions: assign({ refreshQueued: true }) } },
      invoke: {
        src: 'read',
        input: ({ context }) => ({
          filter: context.filter,
          includeDeclined: context.includeDeclined,
          focusResponseId: context.focusResponseId,
        }),
        onDone: {
          target: 'loaded',
          actions: assign({
            data: ({ event }) => event.output.inbox,
            includeDeclined: ({ event }) => event.output.includeDeclined,
            focusResponseId: undefined,
          }),
        },
        onError: {
          target: 'loaded',
          actions: assign({
            data: null,
            error: 'Unable to load service responses.',
            focusResponseId: undefined,
          }),
        },
      },
    },
    // A background refresh after a write or a tab return: the loaded list stays
    // on screen, and a failed refresh keeps it rather than blanking the inbox.
    refreshing: {
      entry: assign({ refreshQueued: false }),
      on: { REFRESH: { actions: assign({ refreshQueued: true }) } },
      invoke: {
        src: 'refresh',
        input: ({ context }) => ({
          filter: context.filter,
          includeDeclined: context.includeDeclined,
          current: context.data ?? { data: [], total: 0, truncated: false },
        }),
        onDone: { target: 'loaded', actions: assign({ data: ({ event }) => event.output }) },
        onError: { target: 'loaded' },
      },
    },
    loaded: {
      always: [
        { guard: ({ context }) => context.refreshQueued, target: 'refreshRequested' },
        { target: 'ready' },
      ],
    },
    ready: { on: { REFRESH: 'refreshRequested' } },
    // With no list loaded (the first read failed), a refresh is a full read.
    refreshRequested: {
      always: [{ guard: 'hasData', target: 'refreshing' }, { target: 'loading' }],
    },
  },
});
